/**
 * Convert clipboard HTML (from Excel, Word, etc.) to clean Markdown.
 * Uses the native DOMParser — zero dependencies.
 *
 * Design goals:
 *  1. Excel tables → markdown pipe tables
 *  2. Word bold/italic/headings/lists → markdown formatting
 *  3. Safe fallback: returns empty string if no meaningful content found,
 *     letting the caller fall back to text/plain.
 */

/** Walk a DOM node tree and produce markdown text. */
function walkNode(node: Node): string {
    // Text node — collapse whitespace but preserve content
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.replace(/\r?\n/g, ' ') ?? ''
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return ''
    const el = node as Element
    const tag = el.tagName.toLowerCase()

    // ── Tables ──────────────────────────────────────────────────
    if (tag === 'table') return convertTable(el)

    // ── Headings ────────────────────────────────────────────────
    if (/^h[1-6]$/.test(tag)) {
        const level = parseInt(tag[1])
        const text = walkChildren(el).trim()
        if (!text) return ''
        return `\n${'#'.repeat(level)} ${text}\n\n`
    }

    // ── Paragraphs & line breaks ────────────────────────────────
    if (tag === 'p') {
        const text = walkChildren(el).trim()
        if (!text) return ''
        return `${text}\n\n`
    }
    if (tag === 'br') return '\n'
    if (tag === 'div') {
        const text = walkChildren(el).trim()
        if (!text) return ''
        return `${text}\n`
    }

    // ── Block-level elements ─────────────────────────────────────
    if (tag === 'blockquote') {
        const inner = walkChildren(el).trim()
        if (!inner) return ''
        // Prefix each line with > 
        return '\n' + inner.split('\n').map(line => `> ${line}`).join('\n') + '\n\n'
    }
    if (tag === 'pre') {
        // Check for <pre><code> pattern → fenced code block
        const codeEl = el.querySelector('code')
        const text = (codeEl || el).textContent || ''
        if (!text.trim()) return ''
        return `\n\`\`\`\n${text.trimEnd()}\n\`\`\`\n\n`
    }
    if (tag === 'hr') return '\n---\n\n'

    // ── Inline formatting ───────────────────────────────────────
    if (tag === 'strong' || tag === 'b') {
        const text = walkChildren(el)
        if (!text.trim()) return text
        return `**${text.trim()}**`
    }
    if (tag === 'em' || tag === 'i') {
        const text = walkChildren(el)
        if (!text.trim()) return text
        return `*${text.trim()}*`
    }
    if (tag === 'del' || tag === 's') {
        const text = walkChildren(el)
        if (!text.trim()) return text
        return `~~${text.trim()}~~`
    }
    if (tag === 'u') {
        // Markdown has no underline — keep text as-is
        return walkChildren(el)
    }
    if (tag === 'code') {
        // Skip if parent is <pre> (handled above as fenced block)
        if (el.parentElement?.tagName.toLowerCase() === 'pre') return el.textContent || ''
        const text = walkChildren(el)
        if (!text.trim()) return text
        return `\`${text.trim()}\``
    }
    if (tag === 'sup') return `^${walkChildren(el).trim()}`
    if (tag === 'sub') return `~${walkChildren(el).trim()}`
    if (tag === 'a') {
        const href = el.getAttribute('href')
        const text = walkChildren(el).trim()
        if (href && text) return `[${text}](${href})`
        return text || ''
    }

    // ── Lists ───────────────────────────────────────────────────
    if (tag === 'ul') return convertList(el, false)
    if (tag === 'ol') return convertList(el, true)
    if (tag === 'li') {
        // Only reached when not processed by convertList (edge case)
        return `- ${walkChildren(el).trim()}\n`
    }

    // ── Skip invisible / metadata elements ──────────────────────
    if (['style', 'script', 'meta', 'link', 'head', 'title'].includes(tag)) return ''

    // ── Default: recurse into children ──────────────────────────
    return walkChildren(el)
}

function walkChildren(el: Element): string {
    let out = ''
    for (const child of Array.from(el.childNodes)) {
        out += walkNode(child)
    }
    return out
}

// ── Table conversion ────────────────────────────────────────────

function convertTable(table: Element): string {
    const rows: string[][] = []

    const trs = table.querySelectorAll('tr')
    for (const tr of Array.from(trs)) {
        const cells: string[] = []
        for (const td of Array.from(tr.querySelectorAll('td, th'))) {
            // Recursively convert cell content (may contain bold, etc.)
            cells.push(walkChildren(td as Element).trim().replace(/\|/g, '\\|').replace(/\n/g, ' '))
        }
        if (cells.length > 0) rows.push(cells)
    }

    if (rows.length === 0) return ''

    // Normalize column count (Excel can have ragged rows)
    const maxCols = Math.max(...rows.map(r => r.length))
    for (const row of rows) {
        while (row.length < maxCols) row.push('')
    }

    // Build markdown table
    const lines: string[] = []
    // Header row
    lines.push('| ' + rows[0].map(c => c || ' ').join(' | ') + ' |')
    // Separator
    lines.push('| ' + rows[0].map(() => '---').join(' | ') + ' |')
    // Data rows
    for (let i = 1; i < rows.length; i++) {
        lines.push('| ' + rows[i].map(c => c || ' ').join(' | ') + ' |')
    }

    return '\n' + lines.join('\n') + '\n\n'
}

// ── List conversion ─────────────────────────────────────────────

function convertList(list: Element, ordered: boolean, depth = 0): string {
    let out = ''
    const indent = '  '.repeat(depth)
    let index = 1
    for (const child of Array.from(list.children)) {
        const tag = child.tagName.toLowerCase()
        if (tag === 'li') {
            // Check for nested lists inside the <li>
            const nestedList = child.querySelector(':scope > ul, :scope > ol')
            // Get text content excluding nested lists
            let text = ''
            for (const c of Array.from(child.childNodes)) {
                if (c.nodeType === Node.ELEMENT_NODE) {
                    const ct = (c as Element).tagName.toLowerCase()
                    if (ct === 'ul' || ct === 'ol') continue // skip nested list here
                }
                text += walkNode(c)
            }
            const prefix = ordered ? `${index}. ` : '- '
            out += `${indent}${prefix}${text.trim()}\n`
            index++
            if (nestedList) {
                const isOrdered = nestedList.tagName.toLowerCase() === 'ol'
                out += convertList(nestedList, isOrdered, depth + 1)
            }
        }
    }
    if (depth === 0) out += '\n'
    return out
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Convert clipboard HTML to markdown.
 * Returns the converted markdown string, or empty string if the HTML
 * contains no meaningful content (caller should fall back to text/plain).
 */
export function htmlToMarkdown(html: string): string {
    if (!html || !html.trim()) return ''

    const doc = new DOMParser().parseFromString(html, 'text/html')

    // Walk from <body>
    const body = doc.body
    if (!body) return ''

    let result = walkNode(body).trim()

    // Sanity check: if the result is too short or identical to what text/plain
    // would give, return empty to let caller use plain text instead.
    // This catches cases like Google Docs wrapping plain text in <span> tags.
    if (!result) return ''

    // Clean up: collapse 3+ consecutive newlines into 2 (preserve paragraph breaks)
    result = result.replace(/\n{3,}/g, '\n\n')

    return result
}

/**
 * Check if the clipboard HTML looks like it contains rich content worth
 * converting (tables, formatting). Returns false for trivial wrappers
 * that would just produce the same result as text/plain.
 */
export function hasRichHtmlContent(html: string): boolean {
    if (!html) return false
    // Quick checks for rich elements before paying for a full parse
    return /<(table|thead|tbody|tr|th|td|strong|b|em|i|h[1-6]|ul|ol|li|blockquote|pre|del|s)\b/i.test(html)
}
